import React, { useRef } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth, errorMessage } from '../context/AuthContext';
import SearchBar from './SearchBar';
import Pagination from './Pagination';
import ShareButton from './ShareButton';
import { NoteRow } from './NotesParts';
import SubjectPicker from './SubjectPicker';
import { useNotesSearch } from '../hooks/useNotesSearch';

// Les dissertations d'UN sujet : barre de recherche, liste « lire plus », pagination.
// admin = true : page de l'espace admin (brouillons compris + ajouter / renommer / supprimer).
// Sur le site public, l'admin connecté voit aussi « modifier » à côté de « lire plus ».
export default function NotesSubjectView({ admin = false }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token, authFetch } = useAuth();
  const [params, setParams] = useSearchParams();
  const query = params.get('q') || '';
  const page = Math.max(1, parseInt(params.get('page'), 10) || 1);
  const [flash, setFlash] = React.useState(null);
  const base = admin ? '/admin/notes' : '/notes';

  const setParam = (key, v, keepPage) => { const n = new URLSearchParams(params); if (v) n.set(key, v); else n.delete(key); if (!keepPage) n.delete('page'); setParams(n, { replace: key === 'q' }); };
  const { data, error, loading, reload } = useNotesSearch(admin ? '/api/admin/notes/search' : '/api/notes', admin ? authFetch : (u, o) => fetch(u, o), query, id, page);

  // tous les sujets, pour la liste déroulante
  const [allSubjects, setAllSubjects] = React.useState([]);
  React.useEffect(() => {
    const f = admin ? authFetch('/api/admin/notes/subjects') : fetch('/api/notes/subjects');
    f.then(r => r.ok ? r.json() : []).then(l => setAllSubjects(l.map(x => ({ id: x.id, name: x.name, count: x.count != null ? x.count : x.note_count })))).catch(() => {});
    // eslint-disable-next-line
  }, [admin, id]);

  // nombre de dissertations du sujet, hors recherche (pour « x résultats sur y »)
  const totalRef = useRef(0);
  if (data && !query.trim()) totalRef.current = data.count;
  const subject = data && data.subject;
  const notes = data ? data.notes : [];

  const call = async (url, method, body, failMsg) => {
    setFlash(null);
    try {
      const r = await authFetch(url, { method, body: body ? JSON.stringify(body) : undefined });
      if (!r.ok) throw new Error(await errorMessage(r));
      return true;
    } catch (e) { setFlash({ type: 'error', text: `${failMsg} : ${e.message}` }); return false; }
  };
  const rename = async () => {
    const name = window.prompt('Nouveau nom du sujet', subject.name);
    if (name && name.trim() && name.trim() !== subject.name && await call(`/api/admin/notes/subjects/${subject.id}`, 'PUT', { name, description: subject.description }, 'Sujet non renommé')) reload();
  };
  const delSubject = async () => {
    if (window.confirm(`Supprimer le sujet « ${subject.name} » ?`) && await call(`/api/admin/notes/subjects/${subject.id}`, 'DELETE', null, 'Suppression impossible')) navigate('/admin/notes');
  };
  const delNote = async (n) => {
    if (window.confirm(`Supprimer la dissertation « ${n.title} » ?`) && await call(`/api/admin/notes/${n.id}`, 'DELETE', null, 'Suppression impossible')) reload();
  };

  const back = <Link to={base} className="note-back">← tous les sujets</Link>;
  if (error) return <section className="section">{back}<div className="empty-state">impossible de charger les dissertations — réessayez dans un instant.</div></section>;
  if (!data) return <section className="section"><div className="empty-state">chargement...</div></section>;
  if (!subject || (!admin && totalRef.current === 0 && !query)) return <section className="section">{back}<div className="empty-state">sujet introuvable.</div></section>;

  return (
    <section className="section fade-in">
      {back}
      <div className="section-head">
        <h2>{subject.name}</h2>
        <span className="head-right">
          <span className="counter">{totalRef.current} dissertation{totalRef.current > 1 ? 's' : ''}</span>
          {!admin && <ShareButton variant="icon" title={`Notes — ${subject.name}`} text={`Les dissertations « ${subject.name} » sur Zahra's Learning`} />}
        </span>
      </div>

      <SubjectPicker subjects={allSubjects} current={subject.id} allHref={base} hrefFor={sid => `${base}/sujet/${sid}`} unit="diss." />
      {flash && <div className={`flash ${flash.type === 'error' ? 'error' : ''}`}>{flash.text}</div>}
      {admin && (
        <div className="note-subject-tools note-subject-tools-top">
          <Link to={`/admin/notes/new?sujet=${subject.id}`} className="btn">+ ajouter une dissertation</Link>
          <button type="button" className="btn secondary" onClick={rename}>renommer le sujet</button>
          <button type="button" className="btn danger" onClick={delSubject} disabled={totalRef.current > 0}
            title={totalRef.current ? 'Supprimez d\'abord les dissertations du sujet' : ''}>supprimer le sujet</button>
        </div>
      )}

      {totalRef.current === 0 && !query ? (
        <div className="empty-state">{admin ? 'Aucune dissertation dans ce sujet pour l\'instant — ajoutez la première.' : 'rien encore ici ✎'}</div>
      ) : (
        <>
          <SearchBar value={query} onChange={v => setParam('q', v)} placeholder={`Rechercher dans « ${subject.name} »…`} label={`Rechercher dans ${subject.name}`}
            count={data.count} total={totalRef.current} unit="dissertation" />
          {notes.length === 0 && !loading && (
            <div className="empty-state search-empty">
              aucune dissertation pour « {query} »
              <button type="button" className="btn secondary" onClick={() => setParam('q', '')}>voir toutes les dissertations</button>
            </div>
          )}
          <ul className={`note-list ${loading ? 'is-loading' : ''}`}>
            {notes.map(n => <NoteRow key={n.id} n={n} showEdit={!!token} onDelete={admin ? delNote : null} />)}
          </ul>
          <Pagination page={data.page} pages={data.pages} onPage={(p) => { setParam('page', p > 1 ? String(p) : '', true); window.scrollTo({ top: 0, behavior: 'smooth' }); }} />
        </>
      )}
    </section>
  );
}
